import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IApplicationStatusDTO } from '@common-frontend-submodule/interfaces/application-status/iapplication-status.interface';
import { IGetApplicationStatusesRequestDTO } from '@common-frontend-submodule/interfaces/application-status/iget-application-statuses-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ApplicationStatusRoutes } from '@common-frontend-submodule/enums/routes/application-status-routes.enum';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApplicationStatusUpdateDTO } from '@common-frontend-submodule/interfaces/application-status/iapplication-status-update.interface';

const apiClient = ApiClient.getInstance();

interface ApplicationStatusContextProps {
  all: () => Promise<IApiResponseGenericDTO<IApplicationStatusDTO[]>>;
  filter: (model: IGetApplicationStatusesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IApplicationStatusDTO>>>;
  getById: (id: string) => Promise<IApiResponseGenericDTO<IApplicationStatusDTO>>;
  update: (applicationStatusId: string, model: IApplicationStatusUpdateDTO) => Promise<IApiResponseGenericDTO<IApplicationStatusDTO>>;
}

const ApplicationStatusContext = createContext<ApplicationStatusContextProps | undefined>(undefined);

export const ApplicationStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (id: string): Promise<IApiResponseGenericDTO<IApplicationStatusDTO>> => {
    return await apiClient.getRequest<IApplicationStatusDTO>(`${ApplicationStatusRoutes.BasePrefix}/${id}`);
  };
  const all = async () => {
    return await apiClient.getRequest<IApplicationStatusDTO[]>(`${ApplicationStatusRoutes.BasePrefix}/${ApplicationStatusRoutes.All}`);
  };
  const filter = async (model: IGetApplicationStatusesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IApplicationStatusDTO>>> => {
    return await apiClient.postRequest<IGetApplicationStatusesRequestDTO, IPaginatedResultDTO<IApplicationStatusDTO>>(
      `${ApplicationStatusRoutes.BasePrefix}/${ApplicationStatusRoutes.List}`,
      model
    );
  };

  const update = async (applicationStatusId: string, model: IApplicationStatusUpdateDTO) => {
    return await apiClient.putRequest<IApplicationStatusUpdateDTO, IApplicationStatusDTO>(`${ApplicationStatusRoutes.BasePrefix}/${applicationStatusId}`, model);
  };

  return (
    <ApplicationStatusContext.Provider value={{ all, filter, getById, update }}>
      {children}
    </ApplicationStatusContext.Provider>
  );
};

export const useApplicationStatusStore = () => {
  const context = useContext(ApplicationStatusContext);
  if (!context) {
    throw new Error('useApplicationStatusStore must be used within an useApplicationStatusStore');
  }
  return context;
};
